/* eslint-disable no-restricted-globals */
import React, { useEffect, useState } from "react";
import "./FileUpload.scss";
import TrashIcon from "@atlaskit/icon/glyph/trash";
import { getUploadUrl } from "../../services/uploads";

type FileUploadProps = {
    file: null | string | File;
    onRemove: () => void;
    onDrop: (event: React.ChangeEvent<HTMLInputElement>) => void;
    errorMessage?: null | string;
};

const FileUploadInput = ({
    onChange,
}: {
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) => (
    <input
        type="file"
        className="file-upload-input"
        name="file"
        accept="image/*"
        onClick={(event) => {
            event.currentTarget.value = "";
        }}
        onChange={onChange}
    />
);

const FileUploadError = ({ errorMessage }: { errorMessage?: null | string }) =>
    errorMessage ? (
        <div className="file-upload-error">{errorMessage}</div>
    ) : null;

const FilledFileUpload = ({
    file,
    previewUrl,
    onRemove,
    onChange,
    errorMessage,
}: {
    file: string | File;
    previewUrl: string | null;
    onRemove: () => void;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    errorMessage?: null | string;
}) => {
    const title =
        typeof file === "string"
            ? "Aktuelles Profilbild"
            : "Ausgewähltes Profilbild";

    return (
        <div className="file-upload-card file-upload-card--filled">
            <div className="file-upload-preview">
                {previewUrl ? (
                    <img
                        className="file-upload-image"
                        src={previewUrl}
                        alt="Profilbild"
                    />
                ) : (
                    <div className="file-upload-image-placeholder">
                        Vorschau wird geladen
                    </div>
                )}
            </div>
            <div className="file-upload-content">
                <div className="file-upload-title">{title}</div>
                <div className="file-upload-note">
                    JPG, PNG oder WEBP, maximal 1 MB
                </div>
                <div className="file-upload-actions">
                    <label className="file-upload-action file-upload-action--secondary">
                        Neues Bild auswählen
                        <FileUploadInput onChange={onChange} />
                    </label>
                    <button
                        type="button"
                        className="file-upload-action file-upload-action--danger"
                        onClick={onRemove}
                    >
                        <TrashIcon
                            primaryColor="currentColor"
                            label="trash"
                            size="medium"
                        />
                        Entfernen
                    </button>
                </div>
                <FileUploadError errorMessage={errorMessage} />
            </div>
        </div>
    );
};

const EmptyFileUpload = ({
    onChange,
    errorMessage,
}: {
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    errorMessage?: null | string;
}) => (
    <label className="file-upload-card file-upload-card--empty">
        <FileUploadInput onChange={onChange} />
        <div className="file-upload-illustration" aria-hidden="true">
            Bild
        </div>
        <div className="file-upload-title">Profilbild hochladen</div>
        <div className="file-upload-subtitle">
            JPG, PNG oder WEBP bis maximal 1 MB
        </div>
        <FileUploadError errorMessage={errorMessage} />
        <span className="file-upload-cta">Bild auswählen</span>
    </label>
);

export const FileUpload = ({
    file,
    onDrop,
    onRemove,
    errorMessage,
}: FileUploadProps) => {
    const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);

    useEffect(() => {
        let objectUrl: string | null = null;

        if (!file || typeof file === "string") {
            setLocalPreviewUrl(null);
        } else {
            objectUrl = URL.createObjectURL(file);
            setLocalPreviewUrl(objectUrl);
        }

        return () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [file]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        event.preventDefault();
        onDrop(event);
    };

    if (file) {
        const previewUrl =
            typeof file === "string" ? getUploadUrl(file) : localPreviewUrl;

        return (
            <FilledFileUpload
                file={file}
                previewUrl={previewUrl}
                onRemove={onRemove}
                onChange={handleChange}
                errorMessage={errorMessage}
            />
        );
    }

    return (
        <EmptyFileUpload onChange={handleChange} errorMessage={errorMessage} />
    );
};
